import { useRef } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import FilesGhostCard from '../../components/Files/FilesGhostCard';
import type File from '../../interfaces/File';

/**
 * Custom hook to manage a floating "ghost" card for file dragging UX.
 * Handles the creation, rendering, and removal of the floating DOM node.
 */
export default function useFilesDragGhostCard() {
  // Reference to the DOM container element for the ghost card
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Reference to the React root used for rendering the ghost card
  const rootRef = useRef<ReturnType<typeof createRoot> | null>(null);

  /**
   * Ensures the container element and React root exist.
   * If not, creates them and appends to document.body.
   * @returns The DOM element and the React root instance.
   */
  const ensure = () => {
    if (!containerRef.current) {
      const el = document.createElement('div');
      document.body.appendChild(el);
      containerRef.current = el;
      rootRef.current = createRoot(el);
    }
    return { el: containerRef.current!, root: rootRef.current! };
  };

  /**
   * Displays the FilesGhostCard with the given file list.
   * @param files - Array of files to display in the ghost card.
   * @returns The HTMLElement of the ghost card (if found), otherwise the container element.
   */
  const show = (files: File[]) => {
    const { el, root } = ensure();
    // Render the ghost card synchronously to ensure DOM is ready for measurements.
    flushSync(() => {
      root.render(<FilesGhostCard files={files} />);
    });
    // Attempt to select the ghost card root for precise DOM access.
    const card = el.querySelector('[data-ghost-card-root]') as HTMLElement | null;
    return card || el;
  };

  /**
   * Hides and cleans up the floating ghost card from the DOM and React root.
   */
  const hide = () => {
    if (rootRef.current) {
      try {
        rootRef.current.unmount();
      } catch {
        // Ignore errors on unmount if already unmounted
      }
      rootRef.current = null;
    }
    if (containerRef.current?.parentNode) {
      containerRef.current.parentNode.removeChild(containerRef.current);
    }
    containerRef.current = null;
  };

  return { show, hide };
}
